import * as React from "react";
import { Disclosure } from "@headlessui/react";
import * as HeroIcons from "@heroicons/react/outline";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { Trans, useTranslation } from "react-i18next";
import Section from "../layout/Section";
import Apply from "./Apply";
import { useI18next } from "gatsby-plugin-react-i18next";
import { graphql, useStaticQuery } from "gatsby";
import { TitleHeading } from "../typography/TitleHeading";
import { useAnimation } from "../../hooks/useAnimate";
import { RawTechIcon } from "../icons/TechIcon";

interface JobPositionProps {
  theme?: "white" | "gray" | "dark";
  defaultOpen: string;
}

const JobPositions: React.FunctionComponent<JobPositionProps> = ({
  defaultOpen,
  theme,
}) => {
  const { language: locale } = useI18next();
  const { t } = useTranslation();
  const { id, animate } = useAnimation("job-positions");

  const {
    allJobPositionsJson: { nodes: jobPositions },
  } = useStaticQuery<{
    allJobPositionsJson: {
      nodes: {
        defaultOpen: boolean;
        name: {
          en: string;
          it: string;
        };
        icon: {
          type: "tech";
          value: string;
        };
        location: string;
        jsonId: string;
        presence: string;
        type: string;
        seniority?: string;
        salary?: [number, number];
        sections: ({
          title: {
            en: string;
            it: string;
          };
        } & (
          | {
              type: "text";
              text: {
                en: string;
                it: string;
              };
            }
          | {
              type: "list";
              list: {
                en: string[];
                it: string[];
              };
            }
        ))[];
      }[];
    };
  }>(graphql`
    query JobPosition {
      allJobPositionsJson(
        sort: { order: ASC, fields: order }
        filter: { enabled: { ne: false } }
      ) {
        nodes {
          defaultOpen
          name {
            en
            it
          }
          icon {
            type
            value
          }
          location
          jsonId
          presence
          type
          seniority
          salary
          sections {
            type
            title {
              en
              it
            }
            text {
              en
              it
            }
            list {
              en
              it
            }
          }
        }
      }
    }
  `);

  return (
    <Section id={id} theme={theme}>
      <div {...animate("fade-down")}>
        <TitleHeading
          tag={t("join:jobPositions.tag")}
          title={t("join:jobPositions.title")}
          center
          bgTag={true}
        />
      </div>
      <div className="w-full my-12">
        <dl className="space-y-5 divide-y divide-gray-200">
          {jobPositions.map((jobPosition, i) => (
            <Disclosure
              as="div"
              id={jobPosition.jsonId}
              key={jobPosition.name.it}
              className="pt-4"
              defaultOpen={
                defaultOpen === jobPosition.jsonId.toString() ||
                jobPosition.defaultOpen
              }
            >
              {({ open }) => (
                <>
                  <dt>
                    <Disclosure.Button className="text-left w-full flex justify-between items-center gap-x-2 md:gap-x-8">
                      <div {...animate("fade-down", i * 100)}>
                        {jobPosition.icon &&
                          (jobPosition.icon.type === "tech" ? (
                            <RawTechIcon
                              icon={jobPosition.icon.value}
                              className="text-gray-500 h-12 w-12 mr-2"
                            />
                          ) : (
                            React.createElement(
                              HeroIcons[jobPosition.icon.value],
                              { className: "text-gray-500 w-12 mr-2" },
                            )
                          ))}
                      </div>
                      <div {...animate("fade-down", i * 100)} className="grow">
                        {jobPosition.seniority && (
                          <span className="text-primary tracking-widest text-xs font-extrabold uppercase">
                            {jobPosition.seniority}
                          </span>
                        )}
                        <p className="text-xl text-gray-900 font-extrabold pr-4">
                          {jobPosition.name[locale]}
                        </p>
                        <p className="text-gray-600 mt-1 whitespace-nowrap text-sm md:text-md">
                          {jobPosition.salary && (
                            <span className="px-2 py-0.5 mr-2 bg-indigo-50 border border-indigo-100 text-indigo-500 text-xs font-bold rounded-xl">
                              {jobPosition.salary[0]}K - {jobPosition.salary[1]}
                              K €
                            </span>
                          )}
                          {jobPosition.location} • {jobPosition.type} •{" "}
                          {jobPosition.presence}
                        </p>
                      </div>
                      <span
                        {...animate("fade-left", i * 100)}
                        className="h-10 flex items-center text-primary"
                      >
                        <ChevronDownIcon
                          className={`${
                            open ? "-rotate-180" : "rotate-0"
                          } h-8 w-8 transform`}
                          aria-hidden="true"
                        />
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel
                    as="dd"
                    className="mt-6 mb-12 md:pl-6 xl:pl-12 grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-24"
                  >
                    <dl className="space-y-6">
                      {jobPosition.sections.map((section) => (
                        <div>
                          <dt className="text-sm font-extrabold text-gray-900">
                            {section.title[locale]}
                          </dt>
                          {section.type === "list" ? (
                            <ol className="mt-1 space-y-1 text-gray-600 list-disc list-outside pl-4">
                              {section.list[locale].map((req) => (
                                <li>{req}</li>
                              ))}
                            </ol>
                          ) : (
                            <dd
                              className="mt-1 text-gray-600 text-justify"
                              dangerouslySetInnerHTML={{
                                __html: section.text[locale],
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </dl>
                    <div className="relative">
                      <Apply
                        classNames="sticky top-12"
                        jobPosition={{
                          id: jobPosition.jsonId,
                          name: jobPosition.name.en,
                        }}
                      />
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
        </dl>
      </div>
      <p className="text-center text-gray-600 mt-6 max-w-prose mx-auto">
        <p className="italic text-sm text-gray-400 mb-4">
          {t("join:jobPositions.disclaimer")}
        </p>
        <Trans i18nKey="join:jobPositions.note" />
      </p>
    </Section>
  );
};

export default JobPositions;
